import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
    Font,
} from "@react-pdf/renderer";

import logo from "../../../images/ensss_logo.png";

/* =========================================================
 * RELEVÉ DE NOTES ANNUEL
 *
 * Même présentation que le relevé de notes officiel par semestre :
 * en-tête bilingue, bloc étudiant, tableau Module / Élément avec les
 * colonnes TP, CC, EXAM, RAT, NFE, Crédit et Décision, moyenne du
 * semestre, crédits, légende et signatures.
 *
 * Une page par semestre de l'année universitaire. Tous les modules et
 * toutes les matières de la classe sont listés, même sans examen passé
 * (note 0).
 * ======================================================= */

Font.register({ family: "Louguiya", src: "/fonts/Louguiya.ttf", fontWeight: "normal" });
Font.register({ family: "Louguiya", src: "/fonts/Louguiya Bold.ttf", fontWeight: "bold" });
Font.register({ family: "LouguiyaFR", src: "/fonts/LouguiyaFR.ttf", fontWeight: "normal" });
Font.register({ family: "LouguiyaFR", src: "/fonts/LouguiyaFR Bold.ttf", fontWeight: "bold" });

const ORDINALS_FR = ["Premier", "Deuxième", "Troisième", "Quatrième", "Cinquième", "Sixième", "Septième", "Huitième"];
const ORDINALS_AR = ["الأول", "الثاني", "الثالث", "الرابع", "الخامس", "السادس", "السابع", "الثامن"];

/* ---------- Helpers ---------- */

const frNumber = (value, decimals) => Number(value).toFixed(decimals).replace(".", ",");

// Note d'élément : entier sans décimale, sinon 2 décimales avec virgule (16 ; 11,42).
const formatMark = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return Number.isInteger(n) ? String(n) : frNumber(n, 2);
};

const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString("fr-FR");
};

const todayLabel = () => new Date().toLocaleDateString("fr-FR");

const semesterIndex = (semester, position) => {
    const match = String(semester.name || "").match(/(\d+)/);
    const n = match ? Number(match[1]) : position + 1;
    // « Semestre 5 » -> 5ᵉ semestre du cursus ; on le ramène à l'ordinal tel quel.
    return n >= 1 && n <= ORDINALS_FR.length ? n : position + 1;
};

const semesterTitles = (semester, position) => {
    if (!semester.id && /sans semestre/i.test(semester.name || "")) {
        return { fr: "Relevé de Notes", ar: "كشف الدرجات", label: "Année" };
    }
    const n = semesterIndex(semester, position);
    return {
        fr: `Relevé de Notes du ${ORDINALS_FR[n - 1]} Semestre`,
        ar: `كشف درجات السداسي ${ORDINALS_AR[n - 1]}`,
        label: `${ORDINALS_FR[n - 1]} Semestre`,
    };
};

/* ---------- Styles ---------- */

const BORDER = 0.8;
const COL = { module: 52, element: 188, mark: 30.5, decision: 92 };

const styles = StyleSheet.create({
    page: {
        paddingTop: 30,
        paddingBottom: 30,
        paddingHorizontal: 36,
        fontFamily: "LouguiyaFR",
        fontSize: 8,
        color: "#000000",
    },
    headerRow: { flexDirection: "row", alignItems: "flex-start", justifyContent: "space-between" },
    headerSide: { width: 210 },
    headerLogo: { width: 70, height: 70, objectFit: "contain" },
    frMinistry: { fontFamily: "LouguiyaFR", fontWeight: "bold", fontSize: 11 },
    frMinistrySub: { fontFamily: "LouguiyaFR", fontWeight: "bold", fontSize: 6, marginTop: 2 },
    frSchool: { fontFamily: "LouguiyaFR", fontSize: 8, marginTop: 5 },
    frDirection: { fontFamily: "LouguiyaFR", fontWeight: "bold", fontSize: 8, marginTop: 2 },
    arLine: { fontFamily: "Louguiya", textAlign: "right" },
    titleBlock: { alignItems: "center", marginTop: 14 },
    titleAr: { fontFamily: "Louguiya", fontWeight: "bold", fontSize: 10 },
    titleFr: { fontFamily: "LouguiyaFR", fontWeight: "bold", fontSize: 12, marginTop: 4 },
    yearRow: { flexDirection: "row", justifyContent: "center", alignItems: "center", marginTop: 6 },
    yearFr: { fontFamily: "LouguiyaFR", fontWeight: "bold", fontSize: 10 },
    yearAr: { fontFamily: "Louguiya", fontWeight: "bold", fontSize: 9, marginLeft: 12 },
    infoBlock: { marginTop: 14 },
    infoRow: { flexDirection: "row", alignItems: "center", marginBottom: 5 },
    infoLabelFr: { fontFamily: "LouguiyaFR", fontWeight: "bold", fontSize: 9 },
    infoValue: { fontFamily: "LouguiyaFR", fontWeight: "bold", fontSize: 10, textAlign: "center" },
    infoLabelAr: { fontFamily: "Louguiya", fontSize: 8, textAlign: "right" },

    table: { marginTop: 12, borderTopWidth: BORDER, borderLeftWidth: BORDER, borderColor: "#000000" },
    row: { flexDirection: "row" },
    cell: {
        borderRightWidth: BORDER,
        borderBottomWidth: BORDER,
        borderColor: "#000000",
        justifyContent: "center",
        paddingVertical: 4,
        paddingHorizontal: 3,
    },
    cellText: { fontFamily: "LouguiyaFR", fontSize: 8 },
    cellBold: { fontFamily: "LouguiyaFR", fontWeight: "bold", fontSize: 8 },
    center: { textAlign: "center" },
    gray: { backgroundColor: "#c9c9c9" },
    moduleCode: { fontFamily: "LouguiyaFR", fontWeight: "bold", fontSize: 6, textAlign: "center" },

    legendRow: { flexDirection: "row", justifyContent: "space-between", marginTop: 10 },
    legendText: { fontFamily: "LouguiyaFR", fontWeight: "bold", fontSize: 8.5, lineHeight: 1.35 },
    dateBlock: { alignItems: "center", marginTop: 2 },
    dateFr: { fontFamily: "LouguiyaFR", fontWeight: "bold", fontSize: 9 },
    dateAr: { fontFamily: "Louguiya", fontSize: 8, marginLeft: 8 },
    signaturesRow: { flexDirection: "row", justifyContent: "space-between", marginTop: 22 },
    signatureBlock: { width: 235 },
    signatureAr: { fontFamily: "Louguiya", fontWeight: "bold", fontSize: 8.5 },
    signatureFr: { fontFamily: "LouguiyaFR", fontWeight: "bold", fontSize: 8.5, marginTop: 2 },
    directorBlock: { alignItems: "center", marginTop: 16 },
    footer: {
        position: "absolute",
        left: 36,
        right: 36,
        bottom: 26,
        flexDirection: "row",
        justifyContent: "space-between",
    },
    footerFr: { fontFamily: "LouguiyaFR", fontWeight: "bold", fontSize: 7.5 },
    footerAr: { fontFamily: "Louguiya", fontSize: 7 },
    emptyText: { fontFamily: "LouguiyaFR", fontSize: 10, marginTop: 20, textAlign: "center" },
});


/* Bandeau affiché sur chaque page quand le relevé contient des résultats non publiés. */
const provisionalStyle = {
    position: "absolute",
    top: 10,
    left: 0,
    right: 0,
    textAlign: "center",
    fontFamily: "LouguiyaFR",
    fontWeight: "bold",
    fontSize: 9,
    color: "#b91c1c",
};

const ProvisionalBanner = () => (
    <Text fixed style={provisionalStyle}>
        DOCUMENT PROVISOIRE - RÉSULTATS NON PUBLIÉS - NE PAS REMETTRE À L'ÉTUDIANT
    </Text>
);

/* ---------- Blocs ---------- */

const Header = () => (
    <View style={styles.headerRow}>
        <View style={styles.headerSide}>
            <Text style={styles.frMinistry}>Ministère de la Santé</Text>
            <Text style={styles.frMinistrySub}>
                Ministère de l'Enseignement Supérieur et de la Recherche Scientifique
            </Text>
            <Text style={styles.frSchool}>Ecole Nationale Supérieure des Sciences de la Santé</Text>
            <Text style={styles.frDirection}>Direction des études</Text>
        </View>
        <Image src={logo} style={styles.headerLogo} />
        <View style={styles.headerSide}>
            <Text style={[styles.arLine, { fontWeight: "bold", fontSize: 11 }]}>وزارة الصحة</Text>
            <Text style={[styles.arLine, { fontWeight: "bold", fontSize: 8, marginTop: 2 }]}>
                وزارة التعليم العالي والبحث العلمي
            </Text>
            <Text style={[styles.arLine, { fontSize: 8, marginTop: 4 }]}>المدرسة الوطنية العليا لعلوم الصحة</Text>
            <Text style={[styles.arLine, { fontSize: 8, marginTop: 2 }]}>إدارة الدروس</Text>
        </View>
    </View>
);

const InfoRow = ({ left, center, right }) => (
    <View style={styles.infoRow}>
        <View style={{ width: 130 }}>
            <Text style={styles.infoLabelFr}>{left}</Text>
        </View>
        <View style={{ flex: 1 }}>
            <Text style={styles.infoValue}>{center}</Text>
        </View>
        <View style={{ width: 125 }}>
            <Text style={styles.infoLabelAr}>{right}</Text>
        </View>
    </View>
);

const StudentBlock = ({ data }) => {
    const levelAndSpecialty = [data.level, data.department].filter((v) => v && v !== "-").join(" ") || "-";
    const registration = data.student.registrationNumber || "-";
    return (
        <View style={styles.infoBlock}>
            <InfoRow left="L'étudiant(e) :" center={data.student.fullName} right="الطالب (ة) :" />
            <View style={styles.infoRow}>
                <View style={{ width: 130 }}>
                    <Text style={styles.infoLabelFr}>Né(e) le :</Text>
                </View>
                <View style={{ flex: 1, flexDirection: "row", justifyContent: "space-around", alignItems: "center" }}>
                    <Text style={styles.infoValue}>{formatDate(data.student.birthDate)}</Text>
                    <Text style={styles.infoLabelFr}>à /en :</Text>
                    <Text style={styles.infoValue}>{data.student.birthPlace || "-"}</Text>
                    {/* Miroir arabe : lieu, « في », date (lecture de droite à gauche). */}
                    <Text style={styles.infoValue}>{data.student.birthPlace || "-"}</Text>
                    <Text style={styles.infoLabelAr}>في</Text>
                    <Text style={styles.infoValue}>{formatDate(data.student.birthDate)}</Text>
                </View>
                <View style={{ width: 90 }}>
                    <Text style={styles.infoLabelAr}>المولود (ة) :</Text>
                </View>
            </View>
            <View style={styles.infoRow}>
                <View style={{ width: 130 }}>
                    <Text style={styles.infoLabelFr}>Inscrit(e) sous le no :</Text>
                </View>
                <View style={{ flex: 1, flexDirection: "row", justifyContent: "space-around" }}>
                    <Text style={styles.infoValue}>{registration}</Text>
                    <Text style={styles.infoValue}>{registration}</Text>
                </View>
                <View style={{ width: 125 }}>
                    <Text style={styles.infoLabelAr}>مسجل (ة) تحت الرقم :</Text>
                </View>
            </View>
            <View style={styles.infoRow}>
                <View style={{ width: 130 }}>
                    <Text style={styles.infoLabelFr}>En :</Text>
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={styles.infoValue}>{levelAndSpecialty}</Text>
                </View>
                <View style={{ width: 125 }}>
                    <Text style={styles.infoLabelAr}>في :</Text>
                </View>
            </View>
        </View>
    );
};

const markCell = (text, key, extra = {}) => (
    <View key={key} style={[styles.cell, { width: COL.mark }, extra]}>
        <Text style={[styles.cellText, styles.center]}>{text}</Text>
    </View>
);

const TableHeader = ({ semesterLabel }) => (
    <View style={styles.row}>
        <View style={[styles.cell, { width: COL.module }]}>
            <Text style={[styles.cellBold, styles.center]}>Module</Text>
        </View>
        <View style={[styles.cell, { width: COL.element }]}>
            <Text style={[styles.cellBold, styles.center]}>Element</Text>
        </View>
        <View>
            <View style={[styles.cell, { width: COL.mark * 6 }]}>
                <Text style={[styles.cellBold, styles.center]}>{semesterLabel}</Text>
            </View>
            <View style={styles.row}>
                {["TP", "CC", "EXAM", "RAT", "NFE", "Credit"].map((label) => (
                    <View key={label} style={[styles.cell, { width: COL.mark }]}>
                        <Text style={[styles.cellBold, styles.center]}>{label}</Text>
                    </View>
                ))}
            </View>
        </View>
        <View style={[styles.cell, { width: COL.decision }]}>
            <Text style={[styles.cellBold, styles.center]}>Décision</Text>
        </View>
    </View>
);

const ModuleRows = ({ module }) => (
    <>
        <View style={styles.row} wrap={false}>
            <View style={[styles.cell, { width: COL.module }]}>
                <Text style={styles.moduleCode}>{module.code}</Text>
            </View>
            <View style={[styles.cell, styles.gray, { width: COL.element }]}>
                <Text style={[styles.cellBold, styles.center]}>{module.name}</Text>
            </View>
            <View style={[styles.cell, styles.gray, { width: COL.mark * 6 }]}>
                <Text style={[styles.cellBold, styles.center]}>{frNumber(module.average, 3)}</Text>
            </View>
            <View style={[styles.cell, styles.gray, { width: COL.decision }]}>
                <Text style={styles.cellText}> </Text>
            </View>
        </View>
        {module.subjects.map((subject) => (
            <View style={styles.row} key={subject.id} wrap={false}>
                <View style={[styles.cell, { width: COL.module }]}>
                    <Text style={styles.cellText}> </Text>
                </View>
                <View style={[styles.cell, { width: COL.element }]}>
                    <Text style={styles.cellText}>{subject.name}</Text>
                </View>
                {markCell("", "tp")}
                {markCell("", "cc")}
                {/* Matière sans examen : note 0 (EXAM et NFE). */}
                {markCell(formatMark(subject.score), "exam", { })}
                {markCell("", "rat")}
                {markCell(formatMark(subject.score), "nfe")}
                {markCell(String(subject.credits ?? 0), "credit")}
                <View style={[styles.cell, { width: COL.decision }]}>
                    <Text style={[styles.cellBold, styles.center]}>{subject.decision}</Text>
                </View>
            </View>
        ))}
    </>
);

const SummaryRow = ({ label, value, decision }) => (
    <View style={styles.row} wrap={false}>
        <View style={[styles.cell, { width: COL.module + COL.element }]}>
            <Text style={[styles.cellBold, styles.center]}>{label}</Text>
        </View>
        <View style={[styles.cell, { width: COL.mark * 6 }]}>
            <Text style={[styles.cellBold, styles.center]}>{value}</Text>
        </View>
        <View style={[styles.cell, { width: COL.decision }]}>
            <Text style={[styles.cellBold, styles.center]}>{decision || ""}</Text>
        </View>
    </View>
);

const Legend = () => (
    <>
        <View style={styles.legendRow}>
            <View>
                <Text style={styles.legendText}>C=Capitalisé</Text>
                <Text style={styles.legendText}>CC=Contrôle Continu</Text>
                <Text style={styles.legendText}>EXAM=Examen</Text>
                <Text style={styles.legendText}>NV=Non Validé</Text>
            </View>
            <View style={styles.dateBlock}>
                <View style={{ flexDirection: "row" }}>
                    <Text style={styles.dateFr}>Nouakchott, le {todayLabel()}</Text>
                    <Text style={styles.dateAr}>انواكشوط بتاريخ</Text>
                </View>
            </View>
            <View>
                <Text style={styles.legendText}>NC=Non Capitalisé</Text>
                <Text style={styles.legendText}>NFE=Note Finale de l'Elément</Text>
                <Text style={styles.legendText}>R=Rattrapage</Text>
                <Text style={styles.legendText}>V=Validé</Text>
            </View>
        </View>
        <View style={styles.signaturesRow}>
            <View style={styles.signatureBlock}>
                <Text style={styles.signatureAr}>رئيس قسم القطب المشترك</Text>
                <Text style={styles.signatureFr}>Chef département de pôle transversal</Text>
            </View>
            <View style={[styles.signatureBlock, { alignItems: "flex-end" }]}>
                <Text style={styles.signatureAr}>رئيس قسم علوم و تقنيات الصحة</Text>
                <Text style={styles.signatureFr}>Chef département des sciences et techniques de la santé</Text>
            </View>
        </View>
        <View style={styles.directorBlock}>
            <Text style={styles.signatureAr}>مدير الدروس</Text>
            <Text style={styles.signatureFr}>Directeur des études</Text>
        </View>
    </>
);

const PageFooter = () => (
    <View style={styles.footer} fixed>
        <Text style={styles.footerFr}>NB: Il ne peut être délivré qu'un seul exemplaire de ce relevé</Text>
        <Text style={styles.footerAr}>تنبيه : لاتسلم إلا نسخة واحدة من هذا الكشف</Text>
    </View>
);

/* ---------- Document ---------- */

const AnnualTranscriptPDF = ({ data }) => {
    const semesters = data?.semesters || [];
    const yearLabel = data?.academicYear?.name || "";
    const isLastOf = (i) => i === semesters.length - 1;

    const renderTitleBlock = (titles) => (
        <>
            <View style={styles.titleBlock}>
                <Text style={styles.titleAr}>{titles.ar}</Text>
                <Text style={styles.titleFr}>{titles.fr}</Text>
            </View>
            <View style={styles.yearRow}>
                <Text style={styles.yearFr}>Année Universitaire :  {yearLabel}</Text>
                <Text style={styles.yearAr}>: السنة الجامعية</Text>
            </View>
        </>
    );

    return (
        <Document
            title="Relevé de notes annuel"
            author="ENSSS"
            subject="Relevé de notes annuel"
            creator="ENSSS"
        >
            {semesters.length === 0 && (
                <Page size="A4" style={styles.page}>
                    {data?.includesProvisional && <ProvisionalBanner />}
                    <Header />
                    {renderTitleBlock({ fr: "Relevé de Notes", ar: "كشف الدرجات" })}
                    <StudentBlock data={data} />
                    <Text style={styles.emptyText}>Aucun module n'est affecté à la classe de cet étudiant.</Text>
                    <PageFooter />
                </Page>
            )}

            {semesters.map((semester, index) => {
                const titles = semesterTitles(semester, index);
                return (
                    <Page key={semester.id || semester.name} size="A4" style={styles.page}>
                        {data?.includesProvisional && <ProvisionalBanner />}
                        <Header />
                        {renderTitleBlock(titles)}
                        <StudentBlock data={data} />

                        <View style={styles.table}>
                            <TableHeader semesterLabel={titles.label} />
                            {semester.modules.map((module) => (
                                <ModuleRows key={module.id} module={module} />
                            ))}
                            <SummaryRow
                                label="Moyenne du semestre | 20"
                                value={frNumber(semester.average, 3)}
                                decision={semester.validated ? "Validé" : "Non validé"}
                            />
                            <SummaryRow
                                label={`Credits | ${semester.credits}`}
                                value={frNumber(semester.creditsAcquired, 2)}
                            />
                            {semesters.length > 1 && isLastOf(index) && (
                                <>
                                    <SummaryRow
                                        label="Moyenne annuelle | 20"
                                        value={frNumber(data.average, 3)}
                                        decision={data.validated ? "Validé" : "Non validé"}
                                    />
                                    <SummaryRow
                                        label={`Credits annuels | ${data.credits}`}
                                        value={frNumber(data.creditsAcquired, 2)}
                                    />
                                </>
                            )}
                        </View>

                        <Legend />
                        <PageFooter />
                    </Page>
                );
            })}
        </Document>
    );
};

export default AnnualTranscriptPDF;